import { useCallback, useState } from "react";

import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";

import { SendIcon, UserPlusIcon } from "lucide-react";

import type { OpenAPI } from "simplydo/interfaces";
import { styled } from "simplydo/react/styled";
import { Button } from "simplydo/react/ui/button";
import { Divider } from "simplydo/react/ui/divider";
import {
  Dropdown,
  DropdownItem,
  DropdownMenu,
} from "simplydo/react/ui/dropdown";
import { H5, Subheader } from "simplydo/react/ui/header";
import { Icon } from "simplydo/react/ui/icon";

import api from "api";
import { useAppSelector } from "store";
import util from "utils/utils";

import { UserChooser } from "components/lib/Choosers";
import { AssessorAudienceWarning } from "components/lib/Choosers/UserChooser/Content/ChallengeAudienceWarning";

import IdeaAssessmentReport from "../../IdeaAssessmentReport";
import type {
  IdeaPreviewChallenge,
  IdeaPreviewIdea,
  IdeaWithAssessmentDetails,
} from "../types";
import { SendAssessmentFeedbackModal } from "./SendAssessmentFeedbackModal";

const SubSegment = styled.div`
  display: flex;
  justify-content: space-between;
  background-color: #f7f7f7;
  border-radius: 5px;
  padding: 5px;
`;

const SubSegmentContent = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 5px;
`;

type ChooserUser = OpenAPI.Schemas["User"] & { isEmailInvitee?: boolean };

type AssessorManagementProps = {
  idea: IdeaWithAssessmentDetails;
  challenge?: IdeaPreviewChallenge;
};

export const AssessorManagement = ({
  idea,
  challenge,
}: AssessorManagementProps) => {
  const { t } = useTranslation();
  const user = useAppSelector((state) => state.user);
  const [assessmentFeedbackModalOpen, setAssessmentFeedbackModalOpen] =
    useState(false);
  // Keep UserChooser outside the round dropdown so the modal is not
  // unmounted when the dropdown closes on item click.
  const [assignRoundId, setAssignRoundId] = useState<string | null>(null);

  const template = challenge?.ownerIdeaTemplate;
  const assessmentRounds = template?.assessments?.rounds || [];
  const [searchParams, setSearchParams] = useSearchParams();

  const addAssessors = useCallback(
    (users: ChooserUser[], assessmentRoundId: string) => {
      if (!challenge) {
        return;
      }

      const usersToAdd = users.filter((assessor) => !assessor.isEmailInvitee);
      if (usersToAdd.length === 0) {
        return;
      }
      api.challenges.assignAssessors(
        challenge._id,
        [idea._id],
        usersToAdd.map((u) => u._id),
        assessmentRoundId,
        ({ ideas }) => {
          const updatedIdea = ideas[0];
          if (updatedIdea) {
            util.onUpdateIdea(idea, { assessments: updatedIdea.assessments });
          }
          toast.success("Assessors updated");
        },
        (err) => toast.error(err.message),
      );
    },
    [idea, challenge],
  );

  const handleAssignComplete = useCallback(
    (users: ChooserUser[], assessmentRoundId: string) => {
      const emailUsers = users.filter((u) => u.isEmailInvitee);
      if (emailUsers.length) {
        api.invitations.createBulk(
          {
            invitees: emailUsers.map((u) => u._id),
            invitationType: "email",
            forType: "ideaAssessor",
            forId: idea.challenge,
            forIdeas: [idea._id],
            ideaDetails: {
              [idea._id]: { assessmentRounds: [assessmentRoundId] },
            },
          },
          () => {
            toast.success(`Invitation${emailUsers.length > 1 ? "s" : ""} sent`);
            api.invalidateQueries(api.invitations.getForType, [
              "ideaAssessor",
              idea.challenge,
            ]);
          },
          () => {},
        );
      }
      addAssessors(users, assessmentRoundId);
      setAssignRoundId(null);
    },
    [addAssessors, idea._id, idea.challenge],
  );

  const currentAssessmentRoundId = searchParams.get("assessmentRound");

  const { assessments } = template ?? {};
  if (!challenge) {
    return null;
  }

  const canEditAssessments =
    util.canManageChallenge(user!, challenge) ||
    util.hasPermission(user!, "challenge.editAssessments", idea.challenge);

  const singleRoundId = assessmentRounds[0]?.id ?? "";

  return (
    <div>
      {canEditAssessments ? (
        <>
          <SubSegment>
            <SubSegmentContent>
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "flex-end",
                }}
              >
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                  {assessmentRounds.length > 1 ? (
                    <Dropdown
                      upward
                      direction="left"
                      trigger={
                        <Button size="small">
                          {
                            <p>
                              <Icon icon={UserPlusIcon} /> Assign assessors
                            </p>
                          }
                        </Button>
                      }
                    >
                      <DropdownMenu>
                        {assessmentRounds.map(
                          (round: { id: string; name: string }) => (
                            <DropdownItem
                              key={round.id}
                              onClick={() => setAssignRoundId(round.id)}
                            >
                              {round.name}
                            </DropdownItem>
                          ),
                        )}
                      </DropdownMenu>
                    </Dropdown>
                  ) : (
                    <Button
                      size="small"
                      onClick={() => setAssignRoundId(singleRoundId)}
                    >
                      {
                        <p>
                          <Icon icon={UserPlusIcon} /> Assign assessors
                        </p>
                      }
                    </Button>
                  )}
                  <Button
                    size="small"
                    onClick={() => setAssessmentFeedbackModalOpen(true)}
                  >
                    {
                      <p>
                        <Icon icon={SendIcon} /> Send feedback
                      </p>
                    }
                  </Button>
                </div>
              </div>
            </SubSegmentContent>
          </SubSegment>
          <UserChooser
            isOpen={assignRoundId !== null}
            onClose={() => setAssignRoundId(null)}
            enabledFeatures={{ search: true, invite: true }}
            onComplete={(users) => {
              if (!assignRoundId) {
                return;
              }
              handleAssignComplete(users as ChooserUser[], assignRoundId);
            }}
            forId={challenge._id}
            afterContent={() =>
              !challenge.visibility?.organisations?.includes(
                user!.organisation,
              ) ? (
                <AssessorAudienceWarning />
              ) : null
            }
            clearOnComplete
            forType="assessor"
            subtitle={`Choose users to assign as assessors of the selected ${t("generic.ideas")}.`}
            confirm="Assign these assessors"
            searchFunction={(term, cb, fail) =>
              api.search.challengeAssessors(
                idea.challenge,
                term,
                ({ users }) => cb(users),
                fail,
              )
            }
          />
          <Divider hidden />
        </>
      ) : null}
      <div>
        {(assessments?.rounds?.length ?? 0) > 0 ? (
          <div style={{ marginBottom: 25 }}>
            <H5>
              Select assessment to review
              <Subheader>
                Automated assessment feedback will be shown in-line with the{" "}
                {t("generic.idea")} content.
              </Subheader>
            </H5>
            <Dropdown
              variant="outline"
              clearable
              fluid
              placeholder="Select assessment"
              value={currentAssessmentRoundId ?? undefined}
              onChange={(e, { value }) => {
                setSearchParams((prev) => {
                  if (!value) {
                    prev.delete("assessmentRound");
                    return prev;
                  }
                  prev.set("assessmentRound", value as string);
                  return prev;
                });
              }}
              options={assessments?.rounds?.map(
                (round: { id: string; name: string }) => ({
                  key: round.id,
                  text: round.name,
                  value: round.id,
                }),
              )}
            />
            <Divider />
          </div>
        ) : null}
      </div>
      <IdeaAssessmentReport idea={idea} challenge={challenge} />
      <SendAssessmentFeedbackModal
        idea={idea as IdeaPreviewIdea & IdeaWithAssessmentDetails}
        challenge={challenge}
        isOpen={assessmentFeedbackModalOpen}
        setOpen={setAssessmentFeedbackModalOpen}
      />
    </div>
  );
};
